// src/components/quote/BookPreview.jsx
//
// Live 3D preview of the book being configured on /quote. Six absolutely-positioned faces
// inside a `transform-style: preserve-3d` box — no WebGL, no libraries. Drag to rotate;
// it idles with a slow auto-spin and resumes that a few seconds after you let go.
//
// The "Open book" toggle swaps the solid box for a flat two-page spread so the paper stock
// and interior print type are actually visible, which the closed book can't show.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import buildBookSpec, { COVER_ART, SPREAD_BODY, SPREAD_TITLE } from './bookPreviewSpec';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Resting angle — enough yaw to show the spine, a little pitch to show the top edge.
const REST_ROT = { x: -10, y: -35 };
const AUTO_SPIN_DEG_PER_FRAME = 0.165;
const RESUME_SPIN_AFTER_MS = 3500;

// `chips` is [label, value] pairs for the spec row under the caption, built by QuotePage
// from the pricing document's option names.
export default function BookPreview({ selections, pricePerUnit, sizeLabel, chips = [] }) {
  const spec = buildBookSpec(selections);
  const { width: W, height: H, depth: D } = spec;

  const [isOpen, setIsOpen] = useState(false);
  const [hint, setHint] = useState('Drag to rotate 360°');

  const bookRef = useRef(null);
  const stageRef = useRef(null);
  const spreadStageRef = useRef(null);

  // Rotation lives in a ref, not state: the auto-spin writes it ~60x a second and a React
  // re-render per frame would re-run the whole quote panel for nothing.
  const rot = useRef({ ...REST_ROT });
  const dragging = useRef(false);
  const lastPointer = useRef({ x: 0, y: 0 });
  const autoSpin = useRef(!prefersReducedMotion());
  const resumeTimer = useRef(null);

  const applyRotation = useCallback(() => {
    const el = bookRef.current;
    if (el) el.style.transform = `rotateX(${rot.current.x}deg) rotateY(${rot.current.y}deg)`;
  }, []);

  useEffect(() => {
    applyRotation();
    if (prefersReducedMotion()) return undefined;

    let frame;
    const tick = () => {
      if (autoSpin.current && !dragging.current && !isOpen) {
        rot.current.y += AUTO_SPIN_DEG_PER_FRAME;
        applyRotation();
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [applyRotation, isOpen]);

  useEffect(() => () => clearTimeout(resumeTimer.current), []);

  const onPointerDown = (e) => {
    dragging.current = true;
    autoSpin.current = false;
    clearTimeout(resumeTimer.current);
    lastPointer.current = { x: e.clientX, y: e.clientY };
    stageRef.current?.classList.add('grabbing');
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setHint('Rotating…');
  };

  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    rot.current.y += dx * 0.4;
    // Pitch is clamped: past ~45° the faces shear badly and the book stops reading as a book.
    rot.current.x = Math.max(-45, Math.min(45, rot.current.x - dy * 0.3));
    applyRotation();
  };

  const endDrag = () => {
    if (!dragging.current) return;
    dragging.current = false;
    stageRef.current?.classList.remove('grabbing');
    setHint('Drag to rotate 360°');
    if (!prefersReducedMotion()) {
      resumeTimer.current = setTimeout(() => { autoSpin.current = true; }, RESUME_SPIN_AFTER_MS);
    }
  };

  // --- open-book spread ---------------------------------------------------------------
  // Scale the two pages to the same trim ratio as the closed book, then shrink the body
  // copy until it fits. Measured rather than computed: the text is real, wrapped text, and
  // its height depends on the font the browser actually loaded.
  const headingRef = useRef(null);
  const bodyRef = useRef(null);
  const pageNumRef = useRef(null);
  const [pageBox, setPageBox] = useState({ w: 0, h: 0, pad: 10 });
  const [stageWidth, setStageWidth] = useState(0);

  // The spread is sized off the stage's measured width, so it has to be re-measured when
  // that width changes — a phone rotating, a window resizing, or the layout reflowing at a
  // breakpoint. Without this the pages keep their old size and overflow the panel.
  useEffect(() => {
    const el = spreadStageRef.current;
    if (!el) return undefined;

    const measure = () => setStageWidth(el.clientWidth);
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const stageW = stageWidth || spreadStageRef.current?.clientWidth || 320;
    // Cap the page height to the stage's own height too, so the shorter stage on a phone
    // doesn't get a spread taller than the box it sits in.
    const stageH = spreadStageRef.current?.clientHeight || 280;
    const scale = Math.min((stageH - 30) / H, (stageW - 30) / (2 * W));
    const w = Math.round(W * scale);
    const h = Math.round(H * scale);
    // Margin stays proportional, so a 1/4 Letter page doesn't get a Letter-sized gutter.
    setPageBox({ w, h, pad: Math.max(7, Math.min(16, Math.round(w * 0.09))) });
  }, [isOpen, W, H, stageWidth]);

  useLayoutEffect(() => {
    if (!isOpen || !bodyRef.current || !pageBox.h) return;
    const body = bodyRef.current;
    const heading = headingRef.current;
    const pageNum = pageNumRef.current;
    const available = pageBox.h - pageBox.pad * 2;

    let fontSize = 11;
    body.style.lineHeight = '1.45';
    // Bounded loop: 11px down to the 5px floor in 0.25px steps can't exceed 24 iterations.
    while (fontSize > 5) {
      body.style.fontSize = `${fontSize}px`;
      if (heading) heading.style.fontSize = `${fontSize * 1.15}px`;
      const used = (heading?.offsetHeight || 0) + body.scrollHeight + (pageNum?.offsetHeight || 0);
      if (used <= available) break;
      fontSize -= 0.25;
    }
  }, [isOpen, pageBox, spec.paperFace]);

  const toggleOpen = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) {
      autoSpin.current = false;
      setHint('Viewing interior paper & layout');
    } else {
      setHint('Drag to rotate 360°');
      if (!prefersReducedMotion()) {
        resumeTimer.current = setTimeout(() => { autoSpin.current = true; }, RESUME_SPIN_AFTER_MS);
      }
    }
  };

  // Each face is centred on the box origin, then pushed out along its own axis by half the
  // relevant dimension — front/back by depth, spine/fore-edge by width, top/bottom by height.
  const face = (styles) => ({ position: 'absolute', top: 0, left: 0, ...styles });
  const paperRules = 'repeating-linear-gradient(90deg, transparent 0 3px, rgba(0,0,0,0.07) 3px 4px)';

  return (
    <div className="preview-panel reveal">
      <div className="pv-head">
        <span className="pv-label"><span className="pv-live-dot" />Live preview</span>
        <span className="pv-label">
          {pricePerUnit == null ? '—' : `$${pricePerUnit.toFixed(2)} / book`}
        </span>
      </div>

      <div className="preview-stage" ref={spreadStageRef}>
        {!isOpen && (
          <div
            className="book-stage-3d"
            ref={stageRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
            onPointerCancel={endDrag}
          >
            <div className="book-3d" ref={bookRef}>
              <div
                className="book-face face-front"
                style={face({
                  width: W,
                  height: H,
                  backgroundColor: spec.coverFill,
                  backgroundImage: [spec.sheen, spec.flapCrease].filter((v) => v && v !== 'none').join(', ') || 'none',
                  transform: `translate(-50%,-50%) translateZ(${D / 2}px)`,
                  color: spec.titleFill,
                })}
              >
                <img className="face-illustration" src={COVER_ART} alt="" />
              </div>

              <div
                className="book-face face-back"
                style={face({
                  width: W,
                  height: H,
                  backgroundColor: spec.coverFill,
                  transform: `translate(-50%,-50%) rotateY(180deg) translateZ(${D / 2}px)`,
                })}
              >
                <div className="fb-barcode" />
              </div>

              <div
                className="book-face face-spine"
                style={face({
                  width: D,
                  height: H,
                  backgroundColor: spec.spineFill,
                  backgroundImage: spec.spinePattern,
                  transform: `translate(-50%,-50%) rotateY(-90deg) translateZ(${W / 2}px)`,
                })}
              >
                <span className="face-spine-label" style={{ color: spec.spineLabelFill }}>
                  ReVisionari
                </span>
              </div>

              <div
                className="book-face face-fore"
                style={face({
                  width: D,
                  height: H,
                  backgroundColor: spec.paperEdge,
                  backgroundImage: 'repeating-linear-gradient(180deg, transparent 0 3px, rgba(0,0,0,0.09) 3px 4px)',
                  transform: `translate(-50%,-50%) rotateY(90deg) translateZ(${W / 2}px)`,
                })}
              >
                <span className="face-page-label">{spec.pages} pp</span>
              </div>

              <div
                className="book-face face-top"
                style={face({
                  width: W,
                  height: D,
                  backgroundColor: spec.paperFace,
                  backgroundImage: paperRules,
                  transform: `translate(-50%,-50%) rotateX(90deg) translateZ(${H / 2}px)`,
                })}
              />
              <div
                className="book-face face-bottom"
                style={face({
                  width: W,
                  height: D,
                  backgroundColor: spec.paperFace,
                  backgroundImage: paperRules,
                  transform: `translate(-50%,-50%) rotateX(-90deg) translateZ(${H / 2}px)`,
                })}
              />
            </div>
          </div>
        )}

        {isOpen && (
          <div className="book-spread">
            <div
              className="spread-page page-left"
              style={{ width: pageBox.w, height: pageBox.h, background: spec.paperFace }}
            >
              <img
                className="pl-illustration"
                src={COVER_ART}
                alt=""
                style={{ filter: spec.printFilter }}
              />
            </div>
            <div
              className="spread-page page-right"
              style={{
                width: pageBox.w,
                height: pageBox.h,
                background: spec.paperFace,
                padding: pageBox.pad,
              }}
            >
              <div className="pr-heading" ref={headingRef}>{SPREAD_TITLE}</div>
              <div className="pr-body" ref={bodyRef}>{SPREAD_BODY}</div>
              <div className="pr-page-num" ref={pageNumRef}>1</div>
            </div>
          </div>
        )}
      </div>

      <div className="preview-controls">
        <button type="button" className="pv-flip-btn" onClick={toggleOpen}>
          {isOpen ? 'Close book' : 'Open book'}
        </button>
        <span className="pv-hint">{hint}</span>
      </div>

      <div className="pv-legend">
        <span className="pv-legend-item">
          <span className="pv-swatch" style={{ background: spec.spineFill }} />Spine / binding
        </span>
        <span className="pv-legend-item">
          <span className="pv-swatch" style={{ background: spec.paperEdge }} />Interior paper
        </span>
        <span className="pv-legend-item">
          <span className="pv-swatch" style={{ background: spec.coverFill }} />Cover
        </span>
      </div>

      <div className="pv-caption">
        <div className="pv-title">{sizeLabel}</div>
      </div>

      {chips.length > 0 && (
        <div className="pv-spec-chips">
          {chips.map(([label, value]) => (
            <span className="pv-chip" key={label}>{label} <b>{value}</b></span>
          ))}
        </div>
      )}
    </div>
  );
}
